import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { Modal } from '../../../components/ui/Modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/Table';
import {
  deleteGradeFeatureAllocations,
  getGradeFeatureAllocations,
  putGradeFeatureAllocations,
} from '../../../modules/admin/api/school.client';
import { getMyFeatureAccess } from '../../../modules/platform/api/featureAccess';
import type { GradeAllocationMode } from '../../../modules/platform/types/gradeFeatureAllocation';
import type { GradeAllocatableFeatureKey } from '../../../modules/platform/types/featureAccess';
import { showErrorToast, showSuccessToast } from '../../../utils/errorHandler';
import { toastManager } from '../../../utils/notifications';
import {
  ALLOCATABLE_FEATURE_LABELS,
  GRADE_KEYS,
  ORDERED_ALLOCATABLE_KEYS,
  buildPayload,
  cloneMatrix,
  matrixFromByGrade,
  stableFingerprint,
} from '../gradeAllocationConfig';

function ownedForAllocatable(schoolFeatureAccess: Record<string, boolean>): Record<string, boolean> {
  const o: Record<string, boolean> = {};
  for (const k of ORDERED_ALLOCATABLE_KEYS) o[k] = schoolFeatureAccess[k] === true;
  return o;
}

export interface GradeFeatureAllocationSectionProps {
  schoolId: string;
}

export const GradeFeatureAllocationSection: React.FC<GradeFeatureAllocationSectionProps> = ({ schoolId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearModalOpen, setClearModalOpen] = useState(false);

  const [schoolOwned, setSchoolOwned] = useState<Record<string, boolean>>({});

  const [serverVersion, setServerVersion] = useState(0);
  const [snapshotMode, setSnapshotMode] = useState<GradeAllocationMode>('strict');
  const [snapshotMatrix, setSnapshotMatrix] = useState(() => cloneMatrix(matrixFromByGrade({})));

  const [draftMode, setDraftMode] = useState<GradeAllocationMode>('strict');
  const [draftMatrix, setDraftMatrix] = useState(() => cloneMatrix(matrixFromByGrade({})));

  const applyServerState = useCallback((data: { version: number; mode: GradeAllocationMode; by_grade: Record<string, string[]> }) => {
    setServerVersion(data.version);
    setSnapshotMode(data.mode);
    setSnapshotMatrix(cloneMatrix(matrixFromByGrade(data.by_grade)));
    setDraftMode(data.mode);
    setDraftMatrix(cloneMatrix(matrixFromByGrade(data.by_grade)));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fa, alloc] = await Promise.all([getMyFeatureAccess(), getGradeFeatureAllocations(schoolId)]);
      setSchoolOwned(ownedForAllocatable(fa.school_feature_access ?? {}));
      applyServerState(alloc);
    } catch (e) {
      showErrorToast(e);
    } finally {
      setLoading(false);
    }
  }, [schoolId, applyServerState]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const anyOwnedFeature = useMemo(
    () => ORDERED_ALLOCATABLE_KEYS.some((k) => schoolOwned[k]),
    [schoolOwned]
  );

  const dirty = useMemo(
    () =>
      stableFingerprint(serverVersion, draftMode, draftMatrix, schoolOwned) !==
      stableFingerprint(serverVersion, snapshotMode, snapshotMatrix, schoolOwned),
    [serverVersion, draftMode, draftMatrix, snapshotMode, snapshotMatrix, schoolOwned]
  );

  const configuredGradeCount = useMemo(
    () =>
      GRADE_KEYS.filter((g) =>
        ORDERED_ALLOCATABLE_KEYS.some((k) => draftMatrix[g]?.has(k) && schoolOwned[k])
      ).length,
    [draftMatrix, schoolOwned]
  );

  const handleReset = () => {
    setDraftMode(snapshotMode);
    setDraftMatrix(cloneMatrix(snapshotMatrix));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload(serverVersion, draftMode, draftMatrix, schoolOwned);
      const updated = await putGradeFeatureAllocations(schoolId, payload);
      applyServerState(updated);
      showSuccessToast('Grade feature allocations saved.');
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 409) {
        try {
          const fresh = await getGradeFeatureAllocations(schoolId);
          applyServerState(fresh);
          toastManager.info('Reloaded latest allocations. Please review and save again.');
        } catch (inner) {
          showErrorToast(inner);
        }
        return;
      }
      showErrorToast(e);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmClear = async () => {
    setClearing(true);
    try {
      await deleteGradeFeatureAllocations(schoolId);
      const fresh = await getGradeFeatureAllocations(schoolId);
      applyServerState(fresh);
      showSuccessToast('Grade feature allocations cleared.');
      setClearModalOpen(false);
    } catch (e) {
      showErrorToast(e);
    } finally {
      setClearing(false);
    }
  };

  const toggleCell = (grade: string, feature: GradeAllocatableFeatureKey) => {
    if (!schoolOwned[feature]) return;
    setDraftMatrix((m) => {
      const next = cloneMatrix(m);
      const s = new Set(next[grade]);
      if (s.has(feature)) s.delete(feature);
      else s.add(feature);
      next[grade] = s;
      return next;
    });
  };

  const selectAllOwnedForGrade = (grade: string) => {
    setDraftMatrix((m) => {
      const next = cloneMatrix(m);
      const s = new Set<string>();
      for (const k of ORDERED_ALLOCATABLE_KEYS) {
        if (schoolOwned[k]) s.add(k);
      }
      next[grade] = s;
      return next;
    });
  };

  const clearGradeRow = (grade: string) => {
    setDraftMatrix((m) => {
      const next = cloneMatrix(m);
      next[grade] = new Set();
      return next;
    });
  };

  const copyGrade1ToRest = () => {
    const g1 = new Set(draftMatrix['1'] ?? []);
    setDraftMatrix((m) => {
      const next = cloneMatrix(m);
      for (const g of GRADE_KEYS) {
        if (g === '1') continue;
        next[g] = new Set(g1);
      }
      return next;
    });
  };

  const copyGrade1ToThreeToFive = () => {
    const g1 = new Set(draftMatrix['1'] ?? []);
    setDraftMatrix((m) => {
      const next = cloneMatrix(m);
      for (const g of ['3', '4', '5'] as const) {
        next[g] = new Set(g1);
      }
      return next;
    });
  };

  return (
    <>
      <Card className="border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Grade-wise Feature Allocation</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Choose which learner modules each grade may use. Only features your school already has access to can be
            allocated. Ownership is managed by the platform team; contact them to add or remove school-wide modules.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading allocations…</p>
          ) : !anyOwnedFeature ? (
            <div
              className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-100"
              role="status"
            >
              Your school has no allocatable learner modules enabled. You cannot set grade allocations until at least
              one module is enabled for your school. Please contact the platform / dev team.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-600 px-2.5 py-0.5 font-medium text-gray-700 dark:text-gray-300">
                  Mode: {draftMode === 'strict' ? 'Strict' : 'Legacy allow'}
                </span>
                <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-600 px-2.5 py-0.5 font-medium text-gray-700 dark:text-gray-300">
                  {configuredGradeCount} grade{configuredGradeCount === 1 ? '' : 's'} with allocations
                </span>
              </div>

              <fieldset className="space-y-2 border-0 p-0 m-0">
                <legend className="text-sm font-medium text-gray-900 dark:text-white mb-2">Allocation mode</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex cursor-pointer gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 has-[:checked]:border-primary-600 dark:has-[:checked]:border-primary-400">
                    <input
                      type="radio"
                      name="grade-alloc-mode"
                      className="mt-1"
                      checked={draftMode === 'strict'}
                      onChange={() => setDraftMode('strict')}
                    />
                    <span>
                      <span className="font-medium text-gray-900 dark:text-white">Strict</span>
                      <span className="block text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        Only features explicitly allowed per grade are available to students in that grade.
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 has-[:checked]:border-primary-600 dark:has-[:checked]:border-primary-400">
                    <input
                      type="radio"
                      name="grade-alloc-mode"
                      className="mt-1"
                      checked={draftMode === 'legacy_allow'}
                      onChange={() => setDraftMode('legacy_allow')}
                    />
                    <span>
                      <span className="font-medium text-gray-900 dark:text-white">Legacy allow</span>
                      <span className="block text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      Configured grades use the allocation you selected, but unconfigured grades fall back to school-level access.                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="primary" disabled={saving || !dirty} onClick={() => void handleSave()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" disabled={saving || !dirty} onClick={handleReset}>
                  Reset
                </Button>
                <Button type="button" variant="secondary" disabled={saving || clearing} onClick={() => setClearModalOpen(true)}>
                  Clear allocations
                </Button>
              </div>

              {/* <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={copyGrade1ToRest}>
                  Copy grade 1 → grades 2–12
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={copyGrade1ToThreeToFive}>
                  Copy grade 1 → grades 3–5
                </Button>
              </div> */}

              <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 z-10 bg-app-surface-muted dark:bg-gray-900 min-w-[8rem]">
                        Grade
                      </TableHead>
                      <TableHead className="min-w-[7rem]">Actions</TableHead>
                      {ORDERED_ALLOCATABLE_KEYS.map((key) => (
                        <TableHead key={key} className="text-center min-w-[6.5rem] whitespace-normal">
                          <span className="inline-block" title={!schoolOwned[key] ? 'Not licensed for your school' : undefined}>
                            {ALLOCATABLE_FEATURE_LABELS[key]}
                            {!schoolOwned[key] ? (
                              <span className="block text-[10px] font-normal normal-case text-gray-400">(not owned)</span>
                            ) : null}
                          </span>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {GRADE_KEYS.map((grade) => (
                      <TableRow key={grade}>
                        <TableCell className="sticky left-0 z-10 bg-app-surface dark:bg-gray-800 font-medium">
                          Grade {grade}
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              className="text-left text-xs text-primary-600 dark:text-primary-400 hover:underline"
                              onClick={() => selectAllOwnedForGrade(grade)}
                            >
                              Select all (owned)
                            </button>
                            <button
                              type="button"
                              className="text-left text-xs text-gray-600 dark:text-gray-400 hover:underline"
                              onClick={() => clearGradeRow(grade)}
                            >
                              Clear grade
                            </button>
                          </div>
                        </TableCell>
                        {ORDERED_ALLOCATABLE_KEYS.map((feature) => {
                          const owned = schoolOwned[feature];
                          const checked = owned && draftMatrix[grade]?.has(feature);
                          return (
                            <TableCell key={feature} className="text-center align-middle">
                              <div className="flex justify-center py-1">
                                <Switch
                                  checked={!!checked}
                                  disabled={!owned}
                                  onCheckedChange={() => toggleCell(grade, feature)}
                                  aria-label={`Grade ${grade} ${ALLOCATABLE_FEATURE_LABELS[feature]}`}
                                />
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={clearModalOpen} onClose={() => !clearing && setClearModalOpen(false)} title="Clear allocations">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          This removes all grade-level feature allocations for your school. Students may fall back to default platform
          behavior until you save new allocations. Continue?
        </p>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button type="button" variant="secondary" disabled={clearing} onClick={() => setClearModalOpen(false)}>
            Cancel
          </Button>
          <Button type="button" variant="primary" disabled={clearing} onClick={() => void handleConfirmClear()}>
            {clearing ? 'Clearing…' : 'Clear'}
          </Button>
        </div>
      </Modal>
    </>
  );
};
